import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { COMPANY } from "@/lib/siteContent";

export default function Privacy() {
  return (
    <main data-testid="privacy-page" className="bg-white min-h-screen">
      <div className="max-w-3xl mx-auto px-6 md:px-12 py-24 sm:py-32">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm text-[#0F2A44] hover:text-[#D4AF7F] transition-colors mb-12"
          data-testid="privacy-back"
        >
          <ArrowLeft className="h-4 w-4" strokeWidth={1.5} />
          Back to home
        </Link>
        <p className="text-xs uppercase tracking-[0.25em] text-[#6B7280] mb-6">
          Privacy
        </p>
        <h1 className="font-serif text-4xl sm:text-5xl tracking-tight text-[#0F2A44] mb-4 leading-tight">
          Privacy Policy
        </h1>
        <p className="text-sm text-[#6B7280] mb-12">
          Effective Date: June 2, 2026
        </p>

        <div className="space-y-6 text-[#1A1A1A]/85 leading-relaxed">
          <p>
            {COMPANY.legalName} ("Crownpath," "we," "us," or "our") operates{" "}
            {COMPANY.domain} (the "Site"). This Privacy Policy explains how we
            collect, use, and share information about visitors to our Site.
          </p>
          <p>By using the Site, you agree to the practices described below.</p>

          <Section title="1. Information We Collect">
            <SubHeading>a) Information You Provide</SubHeading>
            <p>
              When you submit our contact form, request information, or otherwise
              communicate with us, we collect the information you provide, which
              may include:
            </p>
            <Bullets
              items={[
                "Name",
                "Phone number",
                "Email address",
                "Motor Carrier (MC) number",
                "Equipment type",
                "Any other details you choose to share",
              ]}
            />
            <SubHeading>b) Information Collected Automatically</SubHeading>
            <p>
              When you visit the Site, we and our third-party service providers
              may automatically collect:
            </p>
            <Bullets
              items={[
                "IP address and approximate location",
                "Browser type and version",
                "Device type and operating system",
                "Pages viewed and time spent on the Site",
                "Referring website",
                "Cookies and similar tracking technologies",
              ]}
            />
          </Section>

          <Section title="2. How We Use Information">
            <p>We use the information we collect to:</p>
            <Bullets
              items={[
                "Respond to your inquiries and requests",
                "Provide information about our dispatch services",
                "Communicate with you about potential or existing service relationships",
                "Analyze and improve the performance of our Site",
                "Comply with legal obligations",
                "Protect against fraud, abuse, and security threats",
              ]}
            />
          </Section>

          <Section title="3. How We Share Information">
            <p>
              We do not sell your personal information. We may share information
              with:
            </p>
            <Bullets
              items={[
                "Service providers who help us operate the Site and our business, including hosting providers, email services, analytics providers (such as Google Analytics), and advertising platforms (such as Meta).",
                "Law enforcement or government authorities when required by law or to protect our legal rights.",
                "A successor entity in the event of a merger, acquisition, or sale of all or substantially all of our assets.",
              ]}
            />
          </Section>

          <Section title="4. Cookies and Tracking">
            <p>
              We use cookies and similar technologies to operate the Site, analyze
              usage, and improve user experience. You can disable cookies through
              your browser settings, but some features of the Site may not function
              properly without them.
            </p>
            <p>
              We may use Google Analytics, Meta Pixel, and similar tools to
              understand how visitors interact with the Site. You can opt out of
              Google Analytics tracking at{" "}
              <a
                href="https://tools.google.com/dlpage/gaoptout"
                target="_blank"
                rel="noreferrer"
                className="text-[#0F2A44] border-b-2 border-[#D4AF7F] hover:text-[#D4AF7F] transition-colors"
              >
                tools.google.com/dlpage/gaoptout
              </a>
              .
            </p>
          </Section>

          <Section title="5. Data Retention">
            <p>
              We retain personal information for as long as necessary to provide
              our services, fulfill the purposes described in this Policy, and
              comply with legal obligations. Contact form submissions are
              generally retained for up to three (3) years unless you request
              deletion.
            </p>
          </Section>

          <Section title="6. Data Security">
            <p>
              We use reasonable administrative, technical, and physical safeguards
              to protect personal information. No method of transmission over the
              internet or electronic storage is 100% secure, and we cannot
              guarantee absolute security.
            </p>
          </Section>

          <Section title="7. Your Rights">
            <p>
              Depending on where you live, you may have rights regarding your
              personal information, including the right to:
            </p>
            <Bullets
              items={[
                "Access the personal information we hold about you",
                "Request correction of inaccurate information",
                "Request deletion of your personal information",
                "Opt out of certain uses of your personal information",
                "Receive a copy of your information in a portable format",
              ]}
            />
            <p>
              California residents have additional rights under the California
              Consumer Privacy Act (CCPA) and California Privacy Rights Act
              (CPRA). Residents of the European Economic Area, United Kingdom,
              and certain other jurisdictions may have additional rights under
              applicable data protection laws.
            </p>
            <p>To exercise any of these rights, contact us at the address below.</p>
          </Section>

          <Section title="8. Children's Privacy">
            <p>
              The Site is not intended for individuals under 13 years of age. We
              do not knowingly collect personal information from children under
              13. If we become aware that we have collected such information, we
              will delete it.
            </p>
          </Section>

          <Section title="9. Third-Party Links">
            <p>
              The Site may contain links to third-party websites or services. We
              are not responsible for the privacy practices of those third
              parties. We encourage you to review their privacy policies.
            </p>
          </Section>

          <Section title="10. Changes to This Policy">
            <p>
              We may update this Privacy Policy from time to time. When we do, we
              will revise the "Effective Date" at the top of this page. Material
              changes will be communicated by posting an updated version on the
              Site.
            </p>
          </Section>

          <Section title="11. Contact Us">
            <p>
              If you have questions about this Privacy Policy or our privacy
              practices, contact us at:
            </p>
            <address className="not-italic mt-4 space-y-1">
              <div className="font-medium text-[#0F2A44]">{COMPANY.legalName}</div>
              <div>
                Email:{" "}
                <a
                  href={`mailto:${COMPANY.email}`}
                  className="text-[#0F2A44] border-b-2 border-[#D4AF7F] hover:text-[#D4AF7F] transition-colors"
                >
                  {COMPANY.email}
                </a>
              </div>
              <div>
                Phone:{" "}
                <a
                  href={COMPANY.phoneHref}
                  className="text-[#0F2A44] hover:text-[#D4AF7F] transition-colors"
                >
                  {COMPANY.phone}
                </a>
              </div>
            </address>
          </Section>
        </div>
      </div>
    </main>
  );
}

const Section = ({ title, children }) => (
  <section className="pt-8 space-y-4">
    <h2 className="font-serif text-2xl text-[#0F2A44]">{title}</h2>
    {children}
  </section>
);

const SubHeading = ({ children }) => (
  <h3 className="font-serif text-lg text-[#0F2A44] pt-2">{children}</h3>
);

const Bullets = ({ items }) => (
  <ul className="list-disc pl-6 space-y-2">
    {items.map((item) => (
      <li key={item}>{item}</li>
    ))}
  </ul>
);
